import i18n from "i18n";
import translationUtils from "./translation";

const { getSellerNamespace, getCurrentSellerId } = translationUtils;

export const t_old = (text: string | string[], options?: object) => i18n.t(text, options);

export const isTranslationExist = (text: string | string[], options?: object) => i18n.exists(text, options);

export const tInputPlaceholder = (text: string, originFormat: boolean = false) =>
  t("EnterPlaceholder", {
    field: originFormat ? t(text) : t(text).toLowerCase(),
  });

export const tSelectPlaceholder = (text: string, originFormat: boolean = false) =>
  t("SelectPlaceholder", {
    field: originFormat ? t(text) : t(text).toLowerCase(),
  });

export const tFieldIsRequired = (text: string) =>
  t("FieldIsRequired", {
    field: t(text),
  });

export const tFieldInvalidFormat = (text: string) =>
  t("FieldInvalidFormat", {
    field: t(text),
  });

export const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const tSelectMustBeLessThan = (field: string, lessThan: number) =>
  t("FieldMustBeLessThanXSelected", { field, x: lessThan });

/**
 * Translation function with automatic seller-specific namespace support.
 * Automatically checks for seller-specific translations and falls back to default.
 *
 * @param text - Translation key(s)
 * @param options - i18next translation options
 *
 * IMPORTANT USAGE NOTES:
 *
 * 1. Runtime Usage (Recommended):
 *    Use directly in React components where it evaluates at render time:
 *    ```tsx
 *    function MyComponent() {
 *      return <div>{t('MyKey')}</div>  // Correct
 *    }
 *    ```
 *
 * 2. Module-Level Usage (Constants):
 *    DO NOT use at module initialization time (top-level of files).
 *    The seller context is not available yet, so seller-specific translations won't work.
 *
 *    WRONG:
 *    ```ts
 *    const OPTIONS = [{ label: t('MyKey') }];  // Evaluated at import time
 *    export default { OPTIONS };
 *    ```
 *
 *    CORRECT - Use getter functions for lazy evaluation:
 *    ```ts
 *    const getOptions = () => [{ label: t('MyKey') }];
 *    export default {
 *      get OPTIONS() { return getOptions(); }  // Evaluated when accessed
 *    };
 *    ```
 */
export const t = (text: string | string[], options?: object) => {
  const sellerId = getCurrentSellerId();
  const sellerNamespace = getSellerNamespace(sellerId);

  // Try seller namespace first if available
  if (sellerNamespace) {
    const sellerTranslation = i18n.t(text, {
      ns: sellerNamespace,
      ...options,
      defaultValue: null,
    });

    // If translation found in seller namespace (not null and not the key itself)
    if (sellerTranslation !== null && sellerTranslation !== text) {
      return sellerTranslation;
    }
  }

  // Fallback to default translation
  return i18n.t(text, options);
};
